"use client";

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import type { Trip, TripDay, Accommodation, Destination, Flight, RentalCar, BudgetEntry, DestinationImage, CityHighlight } from "./types";
import { recordDebugEvent } from "./debug";
import { BUILD_HASH } from "./buildInfo";
import {
  CACHE_NAMES,
  SYNC_TS_KEY,
  TRIP_MANIFEST_KEY,
  TRIP_MANIFEST_URL,
  TRIPS_URL,
  tripDataUrl,
  tripSyncTsKey,
} from "./cache-constants";
import {
  fetchManifest,
  handleUnauthorized,
  readCachedJson,
  readCachedTimestamp,
  scheduleIdle,
  updateApp,
  writeCachedJson,
  writeCachedTimestamp,
  type AppliedManifest,
} from "./pwa-refresh";

type DayWithImage = TripDay & { heroImage: DestinationImage | null };
type HighlightWithImage = CityHighlight & { image: DestinationImage | null };

interface TripData {
  trip: Trip;
  syncedAt: string;
  days: DayWithImage[];
  accommodations: Accommodation[];
  destinations: Destination[];
  flights: Flight[];
  rentalCar: RentalCar | null;
  budget: BudgetEntry[];
  highlights: HighlightWithImage[];
  images: DestinationImage[];
}

interface TripDataContextValue {
  data: TripData | null;
  loading: boolean;
  lastSynced: Date | null;
  syncing: boolean;
  /** A newer app build is deployed (update() reloads onto it). Only ever
   * learned during a manual Sync — the manifest it fetches carries the
   * server's build hash; nothing probes in the background. */
  appUpdateAvailable: boolean;
  sync: () => Promise<void>;
  update: () => void;
}

const TripDataContext = createContext<TripDataContextValue>({
  data: null,
  loading: true,
  lastSynced: null,
  syncing: false,
  appUpdateAvailable: false,
  sync: async () => {},
  update: () => {},
});

// The image cache is filled lazily by the SW (CacheFirst on /images/*) —
// nothing in this provider ever touches images: a Sync pulls DATA only, and
// never clears anything.
const DATA_CACHE = CACHE_NAMES.tripData;

type TripManifestResponse = AppliedManifest & { build: string };

/** Read this trip's last-synced timestamp. Syncs are per-trip now (one trip's
 * Sync pulls only that trip), so each trip carries its own stamp; the old
 * app-wide stamp (written when Sync used to clear every trip at once) is the
 * read fallback for data cached by older builds. */
async function readSyncTimestamp(tripId: string): Promise<Date | null> {
  return (
    (await readCachedTimestamp(DATA_CACHE, tripSyncTsKey(tripId))) ??
    (await readCachedTimestamp(DATA_CACHE, SYNC_TS_KEY))
  );
}

/** Merge per-bundle hashes into the applied trip manifest — the record of
 * what THIS device's cache holds. Only ids actually synced are written, so
 * syncing Barcelona never masks Croatia's staleness. */
async function mergeAppliedManifest(schema: number, patch: Record<string, string>): Promise<void> {
  const applied = (await readCachedJson<AppliedManifest>(DATA_CACHE, TRIP_MANIFEST_KEY)) ?? {
    schema,
    generatedAt: "",
    bundles: {},
  };
  if (applied.schema !== schema) {
    // Layout change: stale hashes are meaningless — start over.
    applied.schema = schema;
    applied.bundles = {};
  }
  Object.assign(applied.bundles, patch);
  await writeCachedJson(DATA_CACHE, TRIP_MANIFEST_KEY, applied);
}

export function TripDataProvider({ tripId, children }: { tripId: string; children: ReactNode }) {
  const [data, setData] = useState<TripData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [appUpdateAvailable, setAppUpdateAvailable] = useState(false);

  // Per-trip endpoints + cache keys so each trip caches independently for offline.
  const dataUrl = tripDataUrl(tripId);
  const myBundleId = `trip:${tripId}`;

  // Sync Now — incremental, in place: one tiny manifest request, then pull
  // THIS trip's bundle only if its hash changed (plus the trips list if that
  // changed). No other trips, no images, no cache clearing, no reload.
  const sync = useCallback(async () => {
    setSyncing(true);
    try {
      const m = await fetchManifest<TripManifestResponse>(TRIP_MANIFEST_URL);
      if (m.build && BUILD_HASH && m.build !== BUILD_HASH) setAppUpdateAvailable(true);
      const applied = await readCachedJson<AppliedManifest>(DATA_CACHE, TRIP_MANIFEST_KEY);
      const current = applied?.schema === m.schema ? applied.bundles : {};
      const server = m.bundles ?? {}; // defensive: deploy-skew servers may not speak manifests
      const pulled: string[] = [];

      if (current[myBundleId] !== server[myBundleId]) {
        const t0 = performance.now();
        const res = await fetch(dataUrl, { cache: "reload" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const wireBytes = parseInt(res.headers.get("content-length") ?? "", 10);
        const payload: TripData = await res.json();
        await writeCachedJson(DATA_CACHE, dataUrl, payload);
        recordDebugEvent({
          kind: "trip-data",
          url: dataUrl,
          source: `network→cache:${DATA_CACHE}`,
          durationMs: Math.round(performance.now() - t0),
          status: res.status,
          bytes: Number.isFinite(wireBytes) ? wireBytes : JSON.stringify(payload).length,
          detail: "sync — trip bundle changed",
        });
        setData(payload);
        pulled.push(myBundleId);
      }

      let tripsListCurrent = current.trips === server.trips;
      if (!tripsListCurrent) {
        const res = await fetch(TRIPS_URL, { cache: "reload" });
        if (res.ok) {
          await writeCachedJson(DATA_CACHE, TRIPS_URL, await res.json());
          pulled.push("trips");
          tripsListCurrent = true;
        }
      }

      await mergeAppliedManifest(m.schema, {
        [myBundleId]: server[myBundleId],
        // Only record the trips-list hash when the cache really holds it.
        ...(tripsListCurrent ? { trips: server.trips } : {}),
      });

      const now = new Date();
      setLastSynced(now);
      await writeCachedTimestamp(DATA_CACHE, tripSyncTsKey(tripId), now);
      // No separate "sync" summary row: fetchManifest already logged the
      // manifest check (with its wire size), and each pulled bundle logs its
      // own row — so an up-to-date sync shows exactly one manifest row and a
      // changed one shows manifest + the bundle(s), matching finance Refresh.
    } catch (e) {
      if ((e as { status?: number }).status === 401) {
        handleUnauthorized(window.location.pathname);
        return;
      }
      recordDebugEvent({ kind: "manifest", url: TRIP_MANIFEST_URL, source: "error", detail: `sync failed — ${String(e)}` });
    } finally {
      setSyncing(false);
    }
  }, [tripId, dataUrl, myBundleId]);

  // Explicit app update — SW update + shell-cache drop + reload. Data and
  // image caches untouched.
  const update = useCallback(() => {
    setSyncing(true);
    void updateApp();
  }, []);

  // Initial load — cache first, network ONLY on a true cold start (nothing
  // cached to paint). A cached device makes zero requests: no freshness
  // probe, no interval/focus rechecks — Sync Now is the one way data moves.
  // Data only: images are NOT prefetched — they load lazily as pages render
  // them, and the service worker caches each one on first view (CacheFirst
  // on /images/*).
  useEffect(() => {
    let cancelled = false;
    // Reset view state so the spinner shows while the new trip loads.
    setData(null);
    setLoading(true);
    setLastSynced(null);

    (async () => {
      // Try loading from cache (instant for returning users)
      try {
        const t0 = performance.now();
        const cache = await caches.open(DATA_CACHE);
        const cachedRes = await cache.match(dataUrl);
        if (cachedRes) {
          const text = await cachedRes.text();
          const payload: TripData = JSON.parse(text);
          if (cancelled) return;
          setData(payload);

          recordDebugEvent({
            kind: "trip-data",
            url: dataUrl,
            source: `cache:${DATA_CACHE}`,
            durationMs: Math.round(performance.now() - t0),
            bytes: text.length,
            detail: "app cache-first · images lazy-load on render",
          });

          // Restore this trip's sync timestamp
          const ts = await readSyncTimestamp(tripId);
          if (ts && !cancelled) setLastSynced(ts);

          if (!cancelled) setLoading(false);
          // Cached device: paint and stay put — zero network until Sync Now.
          return;
        }
      } catch {
        // Cache API unavailable — fall through to network
      }

      // Cold start — no cache, fetch from network. Render as soon as data
      // arrives; images load lazily as the pages that use them render.
      try {
        const t0 = performance.now();
        const res = await fetch(dataUrl);
        if (res.status === 401) {
          // Signed out (or a stale cached session): login screen, back here after.
          handleUnauthorized(window.location.pathname);
          return;
        }
        if (!res.ok) throw new Error("Sync failed");
        const wireBytes = parseInt(res.headers.get("content-length") ?? "", 10);
        const payload: TripData = await res.json();
        if (cancelled) return;

        recordDebugEvent({
          kind: "trip-data",
          url: dataUrl,
          source: `network→cache:${DATA_CACHE}`,
          durationMs: Math.round(performance.now() - t0),
          status: res.status,
          bytes: Number.isFinite(wireBytes) ? wireBytes : JSON.stringify(payload).length,
          detail: "cold start — no app cache",
        });

        // Cache the API response (re-serialized: clean headers, no
        // Content-Encoding mismatch on replay)
        await writeCachedJson(DATA_CACHE, dataUrl, payload);

        setData(payload);
        setLoading(false);

        const now = new Date();
        setLastSynced(now);
        await writeCachedTimestamp(DATA_CACHE, tripSyncTsKey(tripId), now);

        // Cold start already paid a full network pull, so record the bundle's
        // manifest hash behind the paint (one tiny request, first visit only)
        // — otherwise the first Sync Now couldn't tell this data is current
        // and would re-download the whole bundle. Trip data is static per
        // build, so the hash describes exactly what was just cached. This is
        // bookkeeping for the data already fetched, not a freshness check —
        // cached loads never make it here.
        scheduleIdle(() => {
          if (cancelled) return;
          void (async () => {
            try {
              const m = await fetchManifest<TripManifestResponse>(TRIP_MANIFEST_URL);
              const hash = m.bundles?.[myBundleId];
              if (hash) await mergeAppliedManifest(m.schema, { [myBundleId]: hash });
            } catch {
              /* no info — the first Sync just re-pulls the bundle */
            }
          })();
        });
      } catch {
        // offline + no cache — nothing to show
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tripId, dataUrl, myBundleId]);

  return (
    <TripDataContext.Provider value={{ data, loading, lastSynced, syncing, appUpdateAvailable, sync, update }}>
      {children}
    </TripDataContext.Provider>
  );
}

export function useTripData() {
  return useContext(TripDataContext);
}
